import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MapPin, Search, Navigation, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

const AIRPORT_CODES = {
  Kigali: "Kigali International Airport (KGL)",
  Musanze: "Volcanoes National Park Area",
  Rubavu: "Gisenyi / Lake Kivu",
  Huye: "Butare / Southern Province",
  Nyagatare: "Eastern Province Gateway",
};

const RWANDA_CITIES = [
  "Kigali","Musanze","Huye","Rubavu","Muhanga","Nyagatare","Rwamagana",
  "Rusizi","Kayonza","Kirehe","Bugesera","Gicumbi","Kamonyi","Ngoma",
  "Rulindo","Gakenke","Burera","Nyabihu","Ngororero","Rutsiro","Karongi",
  "Nyamasheke","Nyanza","Gisagara","Nyaruguru","Ruhango","Gatsibo",
];

const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m of [0, 30]) {
    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    const val = `${hh}:${mm}`;
    const ampm = h < 12 ? "AM" : "PM";
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    TIME_OPTIONS.push({ val, label: `${displayH}:${mm} ${ampm}` });
  }
}

function fuzzyMatch(query, city) {
  if (!query) return true;
  const q = query.toLowerCase().replace(/\s/g, "");
  const c = city.toLowerCase();
  if (c.includes(q)) return true;
  let qi = 0;
  for (let ci = 0; ci < c.length && qi < q.length; ci++) {
    if (c[ci] === q[qi]) qi++;
  }
  return qi === q.length;
}

function todayStr() {
  return new Date().toISOString().split("T")[0];
}

function formatDisplayDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(m)-1]} ${parseInt(d)}, ${y}`;
}

function formatTime(val) {
  if (!val) return "";
  const [h, m] = val.split(":").map(Number);
  const ampm = h < 12 ? "AM" : "PM";
  const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${dh}:${String(m).padStart(2,"0")} ${ampm}`;
}

function MiniCalendar({ value, onChange, minDate }) {
  const today = new Date();
  const initDate = value ? new Date(value + "T00:00:00") : today;
  const [viewYear, setViewYear] = React.useState(initDate.getFullYear());
  const [viewMonth, setViewMonth] = React.useState(initDate.getMonth());
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAYS = ["Su","Mo","Tu","We","Th","Fr","Sa"];
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const minD = minDate ? new Date(minDate + "T00:00:00") : null;
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const selD = value ? new Date(value + "T00:00:00") : null;
  const isSelected = (d) => selD && selD.getFullYear() === viewYear && selD.getMonth() === viewMonth && selD.getDate() === d;
  const isDisabled = (d) => { if (!minD) return false; const cd = new Date(viewYear, viewMonth, d); return cd < minD; };
  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y-1); } else setViewMonth(m => m-1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y+1); } else setViewMonth(m => m+1); };
  const pick = (d) => {
    if (isDisabled(d)) return;
    const mm = String(viewMonth+1).padStart(2,"0");
    const dd = String(d).padStart(2,"0");
    onChange(`${viewYear}-${mm}-${dd}`);
  };
  return (
    <div className="w-full select-none">
      <div className="flex items-center justify-between mb-1.5">
        <button type="button" onClick={prevMonth} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-muted text-sm font-bold">&#8249;</button>
        <span className="text-xs font-semibold">{MONTHS[viewMonth]} {viewYear}</span>
        <button type="button" onClick={nextMonth} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-muted text-sm font-bold">&#8250;</button>
      </div>
      <div className="grid grid-cols-7 gap-0 mb-0.5">
        {DAYS.map(d => <div key={d} className="text-center text-[9px] font-bold text-muted-foreground py-0.5">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0">
        {cells.map((d, i) => d === null ? <div key={i} /> : (
          <button key={i} type="button" onClick={() => pick(d)} disabled={isDisabled(d)}
            className={`w-full aspect-square flex items-center justify-center rounded-full text-[11px] transition-colors ${isSelected(d) ? "bg-[#2A9D8F] text-white font-bold" : ""} ${!isSelected(d) && !isDisabled(d) ? "hover:bg-[#D0F5F2] text-foreground" : ""} ${isDisabled(d) ? "text-muted-foreground/30 cursor-not-allowed" : "cursor-pointer"}`}>
            {d}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function SearchBar({ showTrustSignals = false, initialWhere = "", initialFrom = "", initialUntil = "" }) {
  const navigate = useNavigate();
  const [searchWhere, setSearchWhere] = useState(initialWhere);
  const [searchFrom, setSearchFrom] = useState(initialFrom);
  const [searchFromTime, setSearchFromTime] = useState("10:00");
  const [searchUntil, setSearchUntil] = useState(initialUntil);
  const [searchUntilTime, setSearchUntilTime] = useState("10:00");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [locating, setLocating] = useState(false);
  const [cityCounts, setCityCounts] = useState({});
  const searchBtnRef = useRef(null);

  useEffect(() => {
    api.get("/cars", { params: { limit: 100 } })
      .then(({ data }) => {
        const counts = {};
        data.forEach(c => { if(c.city) { const key = c.city.trim(); counts[key] = (counts[key] || 0) + 1; } });
        setCityCounts(counts);
      }).catch(() => {});
  }, []);

  const allCities = [...new Set([...Object.keys(cityCounts), ...RWANDA_CITIES])];
  const suggestions = allCities.filter(city => fuzzyMatch(searchWhere, city))
    .sort((a, b) => (cityCounts[b] || 0) - (cityCounts[a] || 0)).slice(0, 8);

  const handleCurrentLocation = async () => {
    setLocating(true);
    try {
      const res = await fetch("https://api.bigdatacloud.net/data/reverse-geocode-client?localityLanguage=en");
      const data = await res.json();
      setSearchWhere(data.city || data.locality || "Kigali");
      setShowSuggestions(false);
    } catch { setSearchWhere("Kigali"); }
    finally { setLocating(false); }
  };

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchWhere) params.set("q", searchWhere);
    if (searchFrom) params.set("from", `${searchFrom}T${searchFromTime}`);
    if (searchUntil) params.set("until", `${searchUntil}T${searchUntilTime}`);
    navigate(`/cars?${params.toString()}`);
  };

  return (
    <div className="relative w-full">
      {showSuggestions && <div className="fixed inset-0 z-40" onClick={() => setShowSuggestions(false)} />}
      <div className="bg-white rounded-2xl shadow-2xl relative z-50">
        <div className="flex flex-row items-stretch">
          <div className="relative flex-[1.5] border-r border-gray-200 min-w-0">
            <div className="flex items-center gap-1.5 px-3 py-3 cursor-text h-full" onClick={() => { setShowSuggestions(true); setActiveField(null); }}>
              <MapPin size={13} className="text-[#2A9D8F] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400 leading-none mb-0.5">Where</div>
                {searchWhere && !showSuggestions ? (
                  <div className="text-xs font-semibold text-foreground truncate">{searchWhere}</div>
                ) : (
                  <input type="text" placeholder="City" value={searchWhere}
                    onChange={e => { setSearchWhere(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => { setShowSuggestions(true); setActiveField(null); }}
                    className="w-full bg-transparent outline-none text-xs font-medium placeholder:text-gray-400 min-w-0" />
                )}
              </div>
              {searchWhere && !showSuggestions && (
                <button type="button" onClick={e => { e.stopPropagation(); setSearchWhere(""); setShowSuggestions(true); }}
                  className="text-gray-300 hover:text-gray-500 text-[10px] shrink-0">&#x2715;</button>
              )}
            </div>
            {showSuggestions && (
              <div className="absolute left-0 top-full mt-1 bg-white border border-border rounded-xl shadow-lg z-50 w-64 max-h-52 overflow-y-auto">
                <button type="button" onClick={handleCurrentLocation}
                  className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#D0F5F2] transition-colors text-left border-b border-border">
                  <Navigation size={12} className="text-[#2A9D8F] shrink-0" />
                  <span className="text-xs font-semibold text-[#2A9D8F]">{locating ? "Detecting..." : "Current Location"}</span>
                </button>
                {suggestions.map(city => (
                  <button key={city} type="button"
                    onClick={() => { setSearchWhere(city); setShowSuggestions(false); setActiveField("from"); }}
                    className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted transition-colors text-left border-t border-border/40">
                    <div className="flex items-center gap-1.5">
                      <MapPin size={10} className="text-muted-foreground shrink-0" />
                      <div>
                        <div className="text-xs">{city}{AIRPORT_CODES[city] ? ", Rwanda" : ""}</div>
                        {AIRPORT_CODES[city] && <div className="text-[10px] text-muted-foreground">{AIRPORT_CODES[city]}</div>}
                      </div>
                    </div>
                    {cityCounts[city] && <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded-full text-muted-foreground ml-1 shrink-0">{cityCounts[city]}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="relative flex-1 border-r border-gray-200 min-w-0">
            <button type="button" onClick={() => { setActiveField(activeField === "from" ? null : "from"); setShowSuggestions(false); }}
              className="w-full flex items-center px-3 py-3 text-left h-full min-w-0">
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400 leading-none mb-0.5">Pick-up</div>
                <div className={`text-xs font-medium truncate ${searchFrom ? "text-foreground" : "text-gray-400"}`}>
                  {searchFrom ? `${formatDisplayDate(searchFrom)} · ${formatTime(searchFromTime)}` : "Add dates"}
                </div>
              </div>
            </button>
          </div>
          <div className="relative flex-1 min-w-0">
            <button type="button" onClick={() => { setActiveField(activeField === "until" ? null : "until"); setShowSuggestions(false); }}
              className="w-full flex items-center px-3 py-3 text-left h-full min-w-0">
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400 leading-none mb-0.5">Return</div>
                <div className={`text-xs font-medium truncate ${searchUntil ? "text-foreground" : "text-gray-400"}`}>
                  {searchUntil ? `${formatDisplayDate(searchUntil)} · ${formatTime(searchUntilTime)}` : "Add dates"}
                </div>
              </div>
            </button>
          </div>
          <button type="button" onClick={handleSearch} ref={searchBtnRef}
            className="flex items-center justify-center bg-[#2A9D8F] hover:bg-[#23887A] text-white px-4 rounded-r-2xl transition-all shrink-0">
            <Search size={17} />
          </button>
        </div>
      </div>
      {(activeField === "from" || activeField === "fromTime" || activeField === "until" || activeField === "untilTime") && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4" onClick={() => setActiveField(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-2xl shadow-2xl z-10 w-full max-w-[280px]" onClick={e => e.stopPropagation()}>
            {(activeField === "from" || activeField === "until") && (
              <>
                <div className="px-4 pt-4 pb-2 border-b border-border flex items-center justify-between">
                  <span className="text-sm font-bold">{activeField === "from" ? "Pick-up Date" : "Return Date"}</span>
                  <button type="button" onClick={() => setActiveField(null)} className="text-muted-foreground text-lg">&#x2715;</button>
                </div>
                <div className="p-3">
                  <MiniCalendar
                    value={activeField === "from" ? searchFrom : searchUntil}
                    minDate={activeField === "from" ? todayStr() : (searchFrom || todayStr())}
                    onChange={d => {
                      if (activeField === "from") { setSearchFrom(d); setActiveField("fromTime"); }
                      else { setSearchUntil(d); setActiveField("untilTime"); }
                    }} />
                </div>
              </>
            )}
            {(activeField === "fromTime" || activeField === "untilTime") && (
              <>
                <div className="px-4 pt-4 pb-2 border-b border-border flex items-center justify-between">
                  <span className="text-sm font-bold">{activeField === "fromTime" ? "Pick-up Time" : "Return Time"}</span>
                  <button type="button" onClick={() => setActiveField(null)} className="text-muted-foreground text-lg">&#x2715;</button>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {TIME_OPTIONS.map(t => (
                    <button key={t.val} type="button"
                      onClick={() => {
                        if (activeField === "fromTime") { setSearchFromTime(t.val); setActiveField("until"); }
                        else { setSearchUntilTime(t.val); setActiveField(null); }
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors border-b border-border/30 last:border-0 ${(activeField === "fromTime" ? searchFromTime : searchUntilTime) === t.val ? "bg-[#2A9D8F] text-white font-semibold" : "hover:bg-[#D0F5F2]"}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showTrustSignals && (
        <div className="mt-3 flex items-center justify-center gap-3 text-xs text-white/80 flex-wrap">
          <span className="flex items-center gap-1"><ShieldCheck size={11} /> Verified hosts</span>
          <span className="opacity-40">&bull;</span>
          <span>No hidden fees</span>
          <span className="opacity-40">&bull;</span>
          <span>24/7 support</span>
        </div>
      )}
      {showTrustSignals && (
        <div className="mt-8 sm:mt-12 text-center">
          <Link to="/list-your-car" className="text-base sm:text-lg font-bold text-[#2A9D8F] hover:text-[#23887A] transition-colors">
            Click Here to Upload Your Car for Rent
          </Link>
        </div>
      )}
    </div>
  );
}
